import {
  Box,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import { FormLabel, ProgressLoaders } from 'components'
import { color } from 'config/color'
import {
  getMetricUnit,
  getMetricUnitLabel
} from 'containers/Usage/components/utils'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { defaultOptions, formatValue } from 'utils'
import {
  type BillingData,
  type IPast12MonthsBillData,
  type IPast12MonthsUsageData,
  type UtilityType
} from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { DashboardContainer } from './DashboardContainer'
import { UtilityIcon } from './UtilityIcon'

const months = [
  'none',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

interface DataState {
  usage: {
    dates: string[]
    values: any
  }
  billing: {
    dates: string[]
    values: string[]
  }
}

interface IPowerUsage {
  past12MonthsUsageData: IPast12MonthsUsageData[]
  past12MonthsBillData: IPast12MonthsBillData[]
  isLoading: boolean
}

export const PowerUsage = ({
  past12MonthsUsageData,
  past12MonthsBillData,
  isLoading
}: IPowerUsage): JSX.Element => {
  const { services } = useAuth()
  const isMobile = useMobileBreakpoints()

  const [utilityTab, setUtilityTab] = useState<UtilityType>(services[0])
  const [chartTab, setChartTab] = useState<number>(0)

  const handleChartTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setChartTab(newValue)
  }

  const handleUtilityTabChange = (event: SelectChangeEvent): void => {
    setUtilityTab(event.target.value as UtilityType)
  }

  const initialState: DataState = {
    usage: { dates: [], values: [] },
    billing: { dates: [], values: [] }
  }
  const [data, setData] = useState<DataState>(initialState)

  const date = new Date()
  const groupAndSumBills = (bills: BillingData[]): any => {
    const groupedBills: any = {}

    bills.forEach((bill): any => {
      const billId = bill.billId
      const amount = parseFloat(bill.billAmount)

      if (isEmpty(groupedBills[billId])) {
        groupedBills[billId] = {
          billId,
          billAmount: 0,
          services: [],
          readDate: ''
        }
      }

      groupedBills[billId].billAmount += amount
      groupedBills[billId].services = bill.service
      groupedBills[billId].readDate = bill.readDate
    })

    return Object.values(groupedBills)
  }


  useEffect(() => {
    const cleanData = (): void => {
      const billingDates: string[] = []
      const billingValues: string[] = []
      const usageDates: any[] = []
      const usageValues: any[] = []


      const past12Usage = past12MonthsUsageData?.find(
        (usageData: any) => usageData.service === utilityTab
      )

      const past12BillData = past12MonthsBillData?.find(
        (billData: any) => billData.service === utilityTab
      )

      past12Usage?.usage?.usageHistory?.forEach((history: any) => {
        history?.usageData
          ?.filter((item: any) => new Date(item.period) <= date)
          .forEach((item: any) => {
            const d = item.period.slice(0, 10)
            const newDate = dayjs(d).format('MMM')
            if (!usageDates.includes(newDate)) {
              usageDates.push(`${newDate} ${d.slice(2, 4)}`)
            }
            usageValues.push(parseFloat(item.value))
          })
      })

      const billingData = groupAndSumBills(
        past12BillData?.billing?.billing ?? []
      )

      billingData?.forEach((bill: any) => {
        const date = bill.readDate.split('-')
        const monthInt = parseInt(date[1], 10)
        billingDates.push(`${months[monthInt]} ${date[0][2]}${date[0][3]}`)
        billingValues.push(bill.billAmount)
      })

      setData({
        billing: {
          dates: billingDates,
          values: billingValues
        },
        usage: {
          dates: usageDates,
          values: usageValues
        }
      })
    }
    cleanData()
  }, [utilityTab, chartTab, past12MonthsUsageData, past12MonthsBillData])

  const options: any = [
    {
      ...defaultOptions,
      chart: {
        animations: {
          enabled: true
        }
      },
      series: [
        {
          name: 'Usage',
          data: data.usage.values
        }
      ],
      xaxis: {
        categories: data?.usage?.dates,
        labels: {
          show: true,
          formatter(val: any) {
            if (data.usage.dates?.length === 0) return ''
            return val
          }
        }
      },
      yaxis: {
        labels: {
          show: true,
          formatter(val: any) {
            if (data.usage.dates?.length === 0) return ''
            return val
          }
        }
      },
      tooltip: {
        y: {
          formatter(val: any) {
            return formatValue(val, true, false)
          }
        }
      },
      option: {
        chart: {
          id: 'option-1'
        }
      }
    },
    {
      ...defaultOptions,
      chart: {
        animations: {
          enabled: true
        }
      },
      series: [
        {
          name: 'Monthly Bill',
          data: data?.billing?.values
        }
      ],
      xaxis: {
        categories: data.billing.dates,
        labels: {
          show: true,
          formatter(val: any) {
            if (data.billing.dates.length === 0) return ''
            return val
          }
        }
      },
      yaxis: {
        labels: {
          show: true,
          formatter(val: any) {
            return formatValue(val, false, true)
          }
        }
      },
      tooltip: {
        y: {
          formatter(val: any) {
            return formatValue(val, true, true)
          }
        }
      },
      option: {
        chart: {
          id: 'option-2'
        }
      }
    }
  ]

  if (isLoading) {
    return (
      <DashboardContainer minHeight="40vh">
        <ProgressLoaders height="100%" />
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer minHeight="40vh">
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={1}
        justifyContent="space-between"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          my={1}
        >
          {services?.length === 1
            ? (
              <Box>
                <Typography color={color.black} fontSize={18} fontWeight={700}>
                  Billed Usage History
                </Typography>
                <Typography color={color.grey[700]} fontSize={12}>
                  {`${getMetricUnitLabel(
                    services[0]?.toLowerCase()
                  )} (${getMetricUnit(services[0]?.toLowerCase())})`}
                </Typography>
              </Box>
            )
            : (
              <Box display="flex" width="100%">
                <UtilityIcon type={utilityTab?.toLowerCase()} />
                <Box display="flex" flexDirection="column" width="100%" ml={2}>
                  <FormLabel>Select Service</FormLabel>
                  <Select
                    sx={{ minWidth: '200px' }}
                    value={utilityTab}
                    onChange={handleUtilityTabChange}
                  >
                    {services.map((service) => (
                      <MenuItem key={service} value={service}>
                        <Typography
                          color={color.grey[900]}
                          fontSize={16}
                          fontWeight={500}
                        >{`${service?.charAt(0) + service?.substring(1).toLowerCase()
                          } ${chartTab ? 'Bill' : 'Usage History'}`}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
        >
          <Tabs
            sx={{
              bgcolor: color.background[50],
              color: color.grey[700],
              borderRadius: 1,
              '.Mui-selected': {
                bgcolor: color.background[200],
                color: color.grey[900]
              }
            }}
            value={chartTab}
            onChange={handleChartTabChange}
            TabIndicatorProps={{ style: { display: 'none' } }}
          >
            <Tab sx={{ textTransform: 'none' }} label="Consumption" />
            <Tab sx={{ textTransform: 'none' }} label="Amount" />
          </Tabs>
        </Box>
      </Box>

      <Chart
        options={(chartTab != null) && options[chartTab]}
        series={(chartTab != null) && options[chartTab].series}
        type="bar"
        height="75%"
      />
      <Typography mt={3} fontSize={12}>*Usage is shown in the month it is billed. For example, February usage reflects the service period from January to February that appears on your February bill.
        <br />
        Usage is grouped by billing month. Each bar reflects the service period covered by that month’s bill.
      </Typography>
    </DashboardContainer>
  )
}
